import { z } from 'zod';

export const vector3Schema = z
  .tuple([z.number(), z.number(), z.number()])
  .default([0, 0, 0]);

export const trackingProfileSchema = z.object({
  instruction: z.string().min(1),
  detectionTimeoutMs: z.number().int().positive().default(12000),
  lostTrackingGraceMs: z.number().int().positive().default(2500),
  guideScale: z.number().positive().default(1.04)
});

const baseContentSchema = z.object({
  label: z.string().min(1),
  width: z.number().positive(),
  height: z.number().positive(),
  position: vector3Schema.optional().default([0, 0, 0]),
  rotation: vector3Schema.optional().default([0, 0, 0]),
  scale: vector3Schema.optional().default([1, 1, 1]),
  poster: z.string().optional()
});

export const videoContentSchema = baseContentSchema.extend({
  kind: z.literal('video'),
  src: z.string().min(1),
  loop: z.boolean().default(true),
  withAudio: z.boolean().default(false)
});

export const imageContentSchema = baseContentSchema.extend({
  kind: z.literal('image'),
  src: z.string().min(1)
});

export const html2dContentSchema = baseContentSchema.extend({
  kind: z.literal('html2d'),
  src: z.string().optional(),
  headline: z.string().min(1),
  body: z.string().min(1),
  cta: z.string().optional(),
  theme: z
    .object({
      background: z.string().min(1),
      foreground: z.string().min(1),
      accent: z.string().min(1)
    })
    .default({
      background: '#07131d',
      foreground: '#f8fbff',
      accent: '#7dd3fc'
    })
});

export const arContentSchema = z.discriminatedUnion('kind', [
  videoContentSchema,
  imageContentSchema,
  html2dContentSchema
]);

export const fallbackSchema = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  altContentType: z.enum(['image', 'video', 'link']),
  altContentSrc: z.string().min(1)
});

export const sceneSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  teaser: z.string().min(1),
  targetIndex: z.number().int().min(0),
  targetPreview: z.string().min(1),
  arContent: arContentSchema,
  fallback: fallbackSchema,
  trackingProfile: trackingProfileSchema
});

export const experienceEntrySchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  projectId: z.string().min(1),
  title: z.string().min(1),
  mode: z.enum(['single', 'multi']),
  sceneIds: z.array(z.string().min(1)).min(1),
  mindFile: z.string().min(1),
  maxTrack: z.number().int().positive().default(1),
  audioPolicy: z.literal('tap-to-unlock'),
  introTitle: z.string().min(1),
  introBody: z.string().min(1)
});

export const projectThemeSchema = z.object({
  accent: z.string().min(1),
  accentSoft: z.string().min(1),
  surface: z.string().min(1)
});

export const projectSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  client: z.string().min(1),
  city: z.string().min(1),
  summary: z.string().min(1),
  intro: z.string().min(1),
  siteUrl: z.string().url(),
  theme: projectThemeSchema,
  scenes: z.array(sceneSchema).min(1),
  entries: z.array(experienceEntrySchema).min(1)
});

export type TrackingProfile = z.infer<typeof trackingProfileSchema>;
export type ArContent = z.infer<typeof arContentSchema>;
export type Scene = z.infer<typeof sceneSchema>;
export type ExperienceEntry = z.infer<typeof experienceEntrySchema>;
export type Project = z.infer<typeof projectSchema>;
